import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AuthRepository } from 'app/state/auth.repository';
import { TenantsRepository } from 'app/state/tenants.repository';
import { User, UsersRepository } from 'app/state/users.repository';
import { UsersService } from 'app/api/services/users.service';
import { BlobStorageService } from 'app/shared/azure-blob/blob-storage.service';
import { Table } from 'primeng/table';
import { UserForListDto } from 'app/api/models/user/userForListDto';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { BreadcrumbService } from 'app/modules/shared/components/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  standalone: false,
})
export class UsersListComponent implements OnInit {
  @Output() pageChange = new EventEmitter<number>();
  @Output() delete = new EventEmitter<string>();
  @Output() onUserSelection = new EventEmitter<UserForListDto[]>();

  @Input()
  set clearSelectedUsers(value: UserForListDto[]) {
    this.selectedUsers = value;
    this.clear();
  }
  @Input() innerWidth = 0;
  @Input() users: UserForListDto[] = [];
  @Input() isDeleteDisabled = true;
  @Input() showFilters = true;

  @Input() set search(value: string) {
    this.searchString = (value ?? '').toLowerCase().trim();
    this.applyFilters();
  }
  private searchString: string = '';

  @ViewChild('dt1') dt1!: Table;

  public filteredUsers: UserForListDto[] | null = null;
  public selectedUsers: UserForListDto[] = [];
  public actionConfirmation: UserForListDto | null = null;
  public roles: any[];
  public userGroups: any[];
  public showUserGroups: boolean = false;
  selectedGroup: string | null = null;
  public selectedRole: string | null = null;
  public checked: boolean = false;
  private filtersRestored = false;
  constructor(
    public repo: UsersRepository,
    public tenantRepo: TenantsRepository,
    public service: UsersService,
    public auth: AuthRepository,
    public blobService: BlobStorageService,
    public generalDataRepo: GeneralDataRepository,
    private transloco: TranslocoService,
    private userService: UsersService,
  ) {
    this.userGroups = [];
    this.roles = [
      { label: 'User', value: 'user' },
      { label: 'Admin', value: 'admin' },
      { label: 'Super', value: 'super' },
      { label: 'Partner', value: 'partner' },
    ];
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.users && this.users && this.users.length > 0) {
      this.updateUserGroups();

      const saved = localStorage.getItem('user-filters');

      if (saved && !this.filtersRestored) {
        this.filtersRestored = true;
        this.checked = true;
        const parsed = JSON.parse(saved);
        this.selectedGroup = parsed.selectedGroup || null;
        this.selectedGroupTemp = this.selectedGroup;
        this.selectedRole = parsed.selectedRole || null;
        this.searchString =
          (parsed.searchString || '').toLowerCase().trim() || this.searchString;

        let tempUsers = [...this.users];

        if (this.selectedGroup) {
          if (this.selectedGroup === 'no-group') {
            tempUsers = tempUsers.filter(
              (u) => !u.groupsNames || u.groupsNames.length === 0,
            );
          } else {
            tempUsers = tempUsers.filter((u) =>
              u.groupsNames?.includes(this.selectedGroup!),
            );
          }
        }

        if (this.searchString) {
          tempUsers = tempUsers.filter(
            (user) =>
              user.fullName?.toLowerCase().trim().includes(this.searchString) ||
              user.email?.toLowerCase().trim().includes(this.searchString),
          );
        }

        this.filteredUsers = tempUsers;
        if (this.selectedRole && this.dt1) {
          this.dt1.filter(this.selectedRole, 'rolesNames', 'contains');
        }
      } else if (!this.searchString && !this.selectedGroupTemp) {
        this.filteredUsers = this.users;
      }
      this.applyFilters();
    }

    this.generalDataRepo.allGeneralData$.subscribe((x) => {
      this.showUserGroups = x.activeUserData.tenantSet;
    });
    this.persistFilters();
  }

  ngOnInit(): void {
    this.clear();
    //this.filteredUsers = this.users;
  }

  clear() {
    if (this.dt1) {
      this.dt1.clear();
    }
  }
  onRoleChange(value: string) {
    this.selectedRole = value;
    this.applyFilters();
  }
  handleActionClick(user: UserForListDto) {
    this.delete.emit(user.id);
  }

  onGlobalFilter(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchString = input.value.toLowerCase().trim();

    this.applyFilters();
  }

  onCheckboxToggle() {
    if (this.checked) {
      const filterOptions = {
        searchString: this.searchString,
        selectedGroup: this.selectedGroup,
        selectedRole: this.selectedRole,
      };
      localStorage.setItem('user-filters', JSON.stringify(filterOptions));
    } else if (this.checked == false) {
      localStorage.removeItem('user-filters');
    }
  }

  getSeverity(status: any) {
    switch (status.toLowerCase()) {
      case 'user':
        return 'danger';

      case 'admin':
        return 'success';

      case 'super':
        return 'info';

      case 'partner':
        return 'warn';

      default:
        return 'info';
    }
  }

  getFieldValue(array: any[]) {
    if (array.length > 1) {
      return array[0] + '...';
    }
    return array[0];
  }

  updateUserGroups() {
    const groupsSet = new Set<string>();
    if (!this.users) return;
    this.users.forEach((user) => {
      if (user.groupsNames && Array.isArray(user.groupsNames)) {
        user.groupsNames.forEach((group) => {
          groupsSet.add(group);
        });
      }
    });

    this.userGroups = Array.from(groupsSet).map((group) => ({
      label: group,
      value: group,
    }));
  }
  get userGroupsWithNoGroup() {
    return [
      { label: this.transloco.translate('withoutGroup'), value: 'no-group' },
      ...this.userGroups,
    ];
  }

  selectedGroupTemp: string | null = null;
  filterByGroup(value: string) {
    this.selectedGroup = value;
    this.selectedGroupTemp = value;
    this.applyFilters();
  }

  onSelectionChange(event: any): void {
    this.onUserSelection.emit(this.selectedUsers);
  }

  isAllSelected(): boolean {
    const data = this.filteredUsers ?? [];

    return data.length > 0 && this.selectedUsers.length === data.length;
  }

  toggleAll(event: any) {
    const data = this.filteredUsers ?? [];

    if (event.target.checked) {
      this.selectedUsers = [...data];
    } else {
      this.selectedUsers = [];
    }

    this.onSelectionChange(this.selectedUsers);
  }

  onToggleAll(checked: boolean) {
    const data = this.filteredUsers ?? [];
    this.selectedUsers = checked ? [...data] : [];
    this.onSelectionChange(this.selectedUsers);
  }

  sortField: 'name' | 'roles' | 'groups' | null = null;
  sortDir: 1 | -1 = 1;

  toggleSort(field: 'name' | 'roles' | 'groups') {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 1 ? -1 : 1;
    } else {
      this.sortField = field;
      this.sortDir = 1;
    }

    this.applySortingOnly();
  }

  applySortingOnly() {
    const data = this.filteredUsers ?? [];

    this.filteredUsers = [...data].sort((a, b) => {
      let va = '';
      let vb = '';

      if (this.sortField === 'name') {
        va = `${a.name} ${a.surname}`;
        vb = `${b.name} ${b.surname}`;
      }

      if (this.sortField === 'roles') {
        va = a.rolesNames?.join(', ') || '';
        vb = b.rolesNames?.join(', ') || '';
      }

      if (this.sortField === 'groups') {
        va = a.groupsNames?.join(', ') || '';
        vb = b.groupsNames?.join(', ') || '';
      }

      return va.localeCompare(vb) * this.sortDir;
    });
  }

  private persistFilters() {
    if (!this.checked) return;

    localStorage.setItem(
      'user-filters',
      JSON.stringify({
        searchString: this.searchString,
        selectedGroup: this.selectedGroup,
        selectedRole: this.selectedRole,
      }),
    );
  }

  toggleUser(user: UserForListDto) {
    const index = this.selectedUsers.findIndex((u) => u.id === user.id);

    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }

    this.onSelectionChange(this.selectedUsers);
  }

  toggleSaveFilters() {
    this.checked = !this.checked;
    this.onCheckboxToggle();
  }
  get isFilterSaved(): boolean {
    return this.checked;
  }

  applyFilters() {
    let result = [...this.users];

    if (this.searchString) {
      const term = this.searchString.toLowerCase().trim();

      result = result.filter(
        (user) =>
          user.fullName?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term),
      );
    }

    if (this.selectedGroup && this.selectedGroup !== 'all') {
      if (this.selectedGroup === 'no-group') {
        result = result.filter(
          (u) => !u.groupsNames || u.groupsNames.length === 0,
        );
      } else {
        result = result.filter((u) =>
          u.groupsNames?.includes(this.selectedGroup!),
        );
      }
    }

    if (this.selectedRole && this.selectedRole !== 'all') {
      result = result.filter((user) =>
        (user.rolesNames ?? [])
          .join(', ')
          .toLowerCase()
          .includes(this.selectedRole!.toLowerCase()),
      );
    }

    this.filteredUsers = result;

    if (this.sortField) {
      this.applySortingOnly();
    }

    this.persistFilters();
  }
}
